import { useRef, useState } from "react";
import { ImagePlus, Loader2, Star, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { photoSrc } from "@/components/PhotoUpload";

const MAX = 6;

/** Up to six photos per vehicle. The first one is the cover passengers see in the list. */
export const PhotoGallery = ({ photos = [], onChange, testId = "gallery" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async (files) => {
    const room = MAX - photos.length;
    const picked = Array.from(files || []).slice(0, Math.max(0, room));
    if (!picked.length) return;
    setBusy(true); setError("");
    try {
      const urls = [];
      for (const file of picked) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/uploads/image", body,
          { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange([...photos, ...urls]);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const makeCover = (url) => onChange([url, ...photos.filter((p) => p !== url)]);
  const remove = (url) => onChange(photos.filter((p) => p !== url));

  return (
    <div data-testid={testId}>
      <label className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-slate-500">
        Vehicle photos <span className="text-slate-600">({photos.length}/{MAX})</span>
      </label>
      <div className="flex flex-wrap gap-3">
        {photos.map((url, i) => (
          <div key={url} data-testid={`${testId}-item-${i}`} className="group relative">
            <img src={photoSrc(url)} alt=""
              className={`h-20 w-28 rounded-lg border object-cover ${i === 0 ? "border-gold/60" : "border-ink-600"}`} />
            {i === 0 && (
              <span className="absolute left-1 top-1 rounded bg-ink-900/80 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gold">
                cover
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 rounded-b-lg bg-ink-900/80 py-1 opacity-0 transition-opacity group-hover:opacity-100">
              {i !== 0 && (
                <button type="button" data-testid={`${testId}-cover-${i}`} onClick={() => makeCover(url)}
                  title="Make this the cover" className="text-slate-300 hover:text-gold">
                  <Star className="h-3.5 w-3.5" />
                </button>
              )}
              <button type="button" data-testid={`${testId}-remove-${i}`} onClick={() => remove(url)}
                title="Remove" className="text-slate-300 hover:text-red-400">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {photos.length < MAX && (
          <button type="button" data-testid={`${testId}-add`} disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="flex h-20 w-28 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500 transition-colors hover:border-gold/40 hover:text-gold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            add photo
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
        data-testid={`${testId}-input`} onChange={(e) => add(e.target.files)} />
      <p className="mt-2 text-[11px] text-slate-600">
        First photo is the cover. Passengers can swipe the rest before they book.
      </p>
      {error && <p data-testid={`${testId}-error`} className="mt-2 text-sm text-red-400">{error}</p>}
    </div>
  );
};
